import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="memberships"
export default class extends Controller {
  static targets = ["searchInput", "searchResults", "newUserFields", "existingUserInfo", "existingUserId", "selectedUserName"]

  /*
   * Fires when the admin changes the "Owner Type" radio (User vs Company) on the
   * membership edit form. Fetches the list of valid owners for the selected type and
   * repopulates the owner_id dropdown.
   *
   * Why AJAX: the valid owners differ by type — User owners are the membership's
   * representatives (multiple possible), Company owner is just the one company. Rather
   * than rendering both lists server-side and toggling with CSS, we fetch only what's
   * needed. The membership ID comes from item.dataset.membership (set in the view) so
   * the server knows which membership's representatives/company to return.
   */
  changeOwnerType(e) {
    var item = e.currentTarget

    $.ajax({
      type: "GET",
      url: "/memberships/get_potential_membership_owners",
      data: {
        id: item.dataset.membership,
        kind: item.value
      },
      success: function(data) {
        if(data.status == 200) {
          var owners = JSON.parse(data.owners)

          var options = ''
          owners.forEach(function(owner){
            options += '<option value="' + owner[1] + '">' + owner[0] + '</option>'
          })

          document.getElementById('membership_owner_id').innerHTML = options
        } else {
          alert("Error getting owner list.")
        }
      }
    })
  }

  /*
   * Removes a representative from a membership after a confirmation prompt. The operation
   * only deletes the MembershipUser join record — it does not delete the user account,
   * so the person can still log in and be added to other memberships.
   *
   * We use POST (not DELETE) because some network environments block non-GET/POST methods.
   * The CSRF token is included manually since this is a jQuery AJAX call outside of a form.
   * On success, the server returns the user_id so we can remove the correct DOM element
   * (#representative_{id}) without a full page reload. Both success and error callbacks
   * show user-facing alerts so the admin knows whether the operation succeeded.
   */
  removeRepresentative(e) {
    e.preventDefault()

    var item = e.currentTarget

    var ans = confirm('Are you sure you want to remove this representative?')
    if(ans) {
      $.ajax({
        type: "POST",
        headers: { 'X-CSRF-Token': $('meta[name="csrf-token"]').attr('content') },
        url: "/memberships/remove_representative",
        data: {
          membership_id: item.dataset.membership,
          user_id: item.dataset.user
        },
        success: function(data) {
          if(data.status == 200) {
            document.getElementById('representative_' + data.id).remove()
          } else {
            alert("Failed to remove representative: " + (data.error || "Unknown error"))
          }
        },
        error: function(xhr, status, error) {
          console.error("Request failed:", error)
          alert("Failed to remove representative: " + error)
        }
      })
    }
  }

  /*
   * Live typeahead search for existing users when adding a representative to a membership.
   * Fires on every keystroke in the search input but only triggers the AJAX call when the
   * query is 2+ characters to avoid hammering the server on every keypress.
   *
   * Uses an arrow function for the AJAX success callback so `this` still refers to the
   * Stimulus controller (needed to access Stimulus targets like searchResultsTarget).
   * Each result is rendered as a clickable list-group-item with data attributes for user
   * id/name/email — these are read by selectUser() when the admin clicks a result.
   * When no users are found, we show a hint that a new user can be created via the form
   * below, rather than just showing nothing, to guide the admin's next step.
   */
  searchUsers(e) {
    const query = e.target.value
    const resultsContainer = this.searchResultsTarget

    if (query.length < 2) {
      resultsContainer.innerHTML = ''
      resultsContainer.classList.add('d-none')
      return
    }

    $.ajax({
      type: "GET",
      url: "/memberships/search_users",
      data: { query: query },
      success: (data) => {
        if (data.status == 200 && data.users.length > 0) {
          let html = '<div class="list-group">'
          data.users.forEach((user) => {
            html += `<a href="#" class="list-group-item list-group-item-action" data-action="click->memberships#selectUser" data-user-id="${user.id}" data-user-name="${user.name}" data-user-email="${user.email}">${user.name} (${user.email})</a>`
          })
          html += '</div>'
          resultsContainer.innerHTML = html
          resultsContainer.classList.remove('d-none')
        } else {
          resultsContainer.innerHTML = '<div class="text-muted p-2">No users found. You can create a new user below.</div>'
          resultsContainer.classList.remove('d-none')
        }
      }
    })
  }

  /*
   * Handles clicking a user in the search results dropdown. Transitions the UI from
   * "search mode" into "selected user mode" by:
   *   1. Writing the user's ID into the hidden existingUserId field (submitted with the form)
   *   2. Displaying the selected user's name/email in the confirmation box
   *   3. Hiding the new-user creation form (since we're using an existing user)
   *   4. Clearing and hiding the search results dropdown
   *
   * The hidden field is what actually controls which user gets linked on the server side.
   * The visible "selected user" info panel is purely for confirmation — the admin can
   * click "Change" (clearSelectedUser) to start over if they picked the wrong person.
   */
  selectUser(e) {
    e.preventDefault()
    const userId = e.target.dataset.userId
    const userName = e.target.dataset.userName
    const userEmail = e.target.dataset.userEmail

    // Set the hidden field with the user ID
    this.existingUserIdTarget.value = userId

    // Show the selected user info
    this.selectedUserNameTarget.innerHTML = `<strong>${userName}</strong> (${userEmail})`
    this.existingUserInfoTarget.classList.remove('d-none')

    // Hide the new user form fields
    this.newUserFieldsTarget.classList.add('d-none')

    // Clear and hide search results
    this.searchResultsTarget.innerHTML = ''
    this.searchResultsTarget.classList.add('d-none')
    this.searchInputTarget.value = ''
  }

  /*
   * Resets the user selection UI back to "create new user" mode. Called when the admin
   * clicks "Change" after selecting a user from the search results.
   *
   * Clears the hidden existingUserId field so the form won't try to link an existing user,
   * hides the confirmation panel, and shows the new-user creation fields again.
   * This lets the admin correct a wrong selection without reloading the page.
   */
  clearSelectedUser(e) {
    e.preventDefault()

    // Clear the hidden field
    this.existingUserIdTarget.value = ''

    // Hide the selected user info
    this.existingUserInfoTarget.classList.add('d-none')

    // Show the new user form fields
    this.newUserFieldsTarget.classList.remove('d-none')
  }

  connect() {

  }


}
